import * as React from 'react';
import RunLogsApiService, {
  getNurturingRowKey,
  NurturingPreferenceRow,
  NurturingPreferencesResponse,
  PickDateFilterType,
} from '../apiService/runLogsApiService';

function batchSizesChanged(
  current: Record<string, number>,
  initial: Record<string, number>,
): boolean {
  const keysA = Object.keys(current);
  const keysB = Object.keys(initial);
  if (keysA.length !== keysB.length) return true;
  return keysA.some(k => current[k] !== initial[k]);
}

export function useNurturingCandidatePreferences(dealerId: string, modalOpen: boolean) {
  const [loading, setLoading] = React.useState(false);
  /** Separate loading flag for refresh so content stays visible during refresh. */
  const [refreshLoading, setRefreshLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const [preferenceId, setPreferenceId] = React.useState<string | null>(null);
  const [rows, setRows] = React.useState<NurturingPreferenceRow[]>([]);
  const [ttl, setTtl] = React.useState<number | null>(null);
  const [soldWaitDays, setSoldWaitDays] = React.useState<number | null>(null);
  const [unsoldWaitDays, setUnsoldWaitDays] = React.useState<number | null>(null);
  const [headerRow, setHeaderRow] = React.useState<string[]>([
    'Segment',
    'Available for Nurturing Candidate',
    'Maiq Lead Type',
    'Batch Size',
  ]);
  const [batchSizeByRowKey, setBatchSizeByRowKey] = React.useState<
    Record<string, number>
  >({});
  const [initialBatchSizeByRowKey, setInitialBatchSizeByRowKey] = React.useState<
    Record<string, number>
  >({});
  const [saveLoading, setSaveLoading] = React.useState(false);
  const [rawResponse, setRawResponse] = React.useState<NurturingPreferencesResponse | null>(null);
  const [isAllChecked, setIsAllChecked] = React.useState(false);
  const [initialIsAllChecked, setInitialIsAllChecked] = React.useState(false);
  const [batchSizesBeforeAll, setBatchSizesBeforeAll] = React.useState<Record<string, number> | null>(null);

  const [dateFilterType, setDateFilterType] = React.useState<Exclude<PickDateFilterType, 'ALL'> | null>(null);
  const [startDate, setStartDate] = React.useState<string | null>(null);
  const [endDate, setEndDate] = React.useState<string | null>(null);

  const [initialDateFilterType, setInitialDateFilterType] = React.useState<
    Exclude<PickDateFilterType, 'ALL'> | null
  >(null);
  const [initialStartDate, setInitialStartDate] = React.useState<string | null>(null);
  const [initialEndDate, setInitialEndDate] = React.useState<string | null>(null);

  React.useEffect(() => {
    setPreferenceId(null);
    setRows([]);
    setTtl(null);
    setSoldWaitDays(null);
    setUnsoldWaitDays(null);
    setBatchSizeByRowKey({});
    setInitialBatchSizeByRowKey({});
    setError(null);
    setHeaderRow([
      'Segment',
      'Available for Nurturing Candidate',
      'Maiq Lead Type',
      'Batch Size',
    ]);
    setRawResponse(null);
    setIsAllChecked(false);
    setInitialIsAllChecked(false);
    setBatchSizesBeforeAll(null);

    setDateFilterType(null);
    setStartDate(null);
    setEndDate(null);
    setInitialDateFilterType(null);
    setInitialStartDate(null);
    setInitialEndDate(null);
  }, [dealerId]);

  /**
   * Core fetch. When isRefresh=true:
   * - Passes ?refresh=true query param to backend (bypasses backend cache)
   * - Uses refreshLoading instead of loading so existing data stays visible
   */
  const fetchPreferences = React.useCallback(
    async (isRefresh?: boolean) => {
      if (!dealerId) return;
      if (isRefresh) {
        setRefreshLoading(true);
      } else {
        setLoading(true);
      }
      setError(null);
      try {
        const response = await RunLogsApiService.getNurturingPreferences(dealerId, isRefresh);
        const data = (response?.body ?? response) as
          | NurturingPreferencesResponse
          | null
          | undefined;

        const id = data?._id ?? data?.id ?? null;
        setPreferenceId(typeof id === 'string' ? id : null);

        const props = data?.properties;
        const rawRows = Array.isArray(props?.row) ? props!.row : [];

        setRows(rawRows);
        setTtl(props?.ttl ?? null);
        setSoldWaitDays(props?.sold_wait_days ?? null);
        setUnsoldWaitDays(props?.unsold_wait_days ?? null);

        if (Array.isArray(data?.header_row) && data!.header_row!.length > 0) {
          setHeaderRow(data!.header_row!);
        }

        const sizes: Record<string, number> = {};
        for (const row of rawRows) {
          sizes[getNurturingRowKey(row)] = row.batch_size;
        }
        setBatchSizeByRowKey({ ...sizes });
        setInitialBatchSizeByRowKey({ ...sizes });

        // Store full raw response so callers can include it in API payloads
        setRawResponse(data ?? null);

        const propsAny = (props ?? {}) as any;
        const candidateFilterType: any =
          propsAny?.dateFilterType ??
          propsAny?.pickDateFilterType ??
          propsAny?.date_filter_type ??
          propsAny?.pick_date_filter_type ??
          null;

        const normalizedFilterType: Exclude<PickDateFilterType, 'ALL'> | null =
          candidateFilterType === 'SINCE_DATE' || candidateFilterType === 'DATE_RANGE'
            ? candidateFilterType
            : null;

        const rawStart: any =
          propsAny?.startDate ??
          propsAny?.pickStartDate ??
          propsAny?.start_date ??
          propsAny?.pick_start_date ??
          propsAny?.sinceDate ??
          propsAny?.since_date ??
          null;
        const rawEnd: any =
          propsAny?.endDate ??
          propsAny?.pickEndDate ??
          propsAny?.end_date ??
          propsAny?.pick_end_date ??
          propsAny?.untilDate ??
          propsAny?.until_date ??
          null;

        const derivedType: Exclude<PickDateFilterType, 'ALL'> | null =
          normalizedFilterType ??
          (rawStart && rawEnd ? 'DATE_RANGE' : rawStart ? 'SINCE_DATE' : null);

        const nextStart = typeof rawStart === 'string' && rawStart.trim() ? rawStart : null;
        const nextEnd = typeof rawEnd === 'string' && rawEnd.trim() ? rawEnd : null;
        const finalEnd = derivedType === 'DATE_RANGE' ? nextEnd : null;
        const nextAllChecked = Boolean(
          propsAny?.all ??
            propsAny?.all_checked ??
            propsAny?.allSelected ??
            false,
        );

        setDateFilterType(derivedType);
        setStartDate(nextStart);
        setEndDate(finalEnd);
        setInitialDateFilterType(derivedType);
        setInitialStartDate(nextStart);
        setInitialEndDate(finalEnd);
        setIsAllChecked(nextAllChecked);
        setInitialIsAllChecked(nextAllChecked);
        setBatchSizesBeforeAll(null);

        if (!data && response?.message) {
          setError(response.message);
        }
      } catch (err: any) {
        setPreferenceId(null);
        setRows([]);
        setBatchSizeByRowKey({});
        setInitialBatchSizeByRowKey({});
        setRawResponse(null);
        setError(err?.message || 'Failed to load preferences');
        setIsAllChecked(false);
        setBatchSizesBeforeAll(null);

        setDateFilterType(null);
        setStartDate(null);
        setEndDate(null);
        setInitialDateFilterType(null);
        setInitialStartDate(null);
        setInitialEndDate(null);
        setInitialIsAllChecked(false);
      } finally {
        if (isRefresh) {
          setRefreshLoading(false);
        } else {
          setLoading(false);
        }
      }
    },
    [dealerId],
  );

  React.useEffect(() => {
    if (modalOpen && dealerId) {
      fetchPreferences();
    }
  }, [modalOpen, dealerId, fetchPreferences]);

  /** Calls GET with ?refresh=true — content stays visible, only refresh spinner shows. */
  const refreshData = React.useCallback(() => fetchPreferences(true), [fetchPreferences]);

  const handleDateFilterTypeChange = React.useCallback(
    (nextType: Exclude<PickDateFilterType, 'ALL'> | null) => {
      setDateFilterType(nextType);
      if (nextType === 'SINCE_DATE') {
        setEndDate(null);
      }
      if (!nextType) {
        setStartDate(null);
        setEndDate(null);
      }
    },
    [],
  );

  const updateBatchSize = React.useCallback((rowKey: string, value: number) => {
    setBatchSizeByRowKey(prev => ({ ...prev, [rowKey]: value }));
  }, []);

  const handleAllCheckedChange = React.useCallback(
    (checked: boolean) => {
      if (checked) {
        setBatchSizesBeforeAll({ ...batchSizeByRowKey });
        const next: Record<string, number> = {};
        for (const row of rows) {
          next[getNurturingRowKey(row)] = Math.max(0, row.available_for_nurturing_candidate ?? 0);
        }
        setBatchSizeByRowKey(next);
        setIsAllChecked(true);
        return;
      }

      if (batchSizesBeforeAll) {
        setBatchSizeByRowKey({ ...batchSizesBeforeAll });
      }
      setIsAllChecked(false);
      setBatchSizesBeforeAll(null);
    },
    [batchSizeByRowKey, rows, batchSizesBeforeAll],
  );

  const savePreferences = React.useCallback(async () => {
    if (!preferenceId) throw new Error('Preference ID is required. Load preferences first.');
    setSaveLoading(true);
    try {
      const updatedRows = rows.map(row => ({
        ...row,
        batch_size: batchSizeByRowKey[getNurturingRowKey(row)] ?? row.batch_size,
      }));
      const body: Partial<NurturingPreferencesResponse> = {
        properties: {
          ttl: ttl ?? 0,
          sold_wait_days: soldWaitDays ?? 0,
          unsold_wait_days: unsoldWaitDays ?? 0,
          row: updatedRows,
          // Persist only snake_case filter fields in preferences payload.
          ...(function () {
            const normalizedType = dateFilterType ?? null;
            const normalizedStart = dateFilterType ? startDate ?? null : null;
            const normalizedEnd = dateFilterType === 'DATE_RANGE' ? endDate ?? null : null;
            return {
              all: isAllChecked,
              date_filter_type: normalizedType,
              start_date: normalizedStart,
              end_date: normalizedEnd,
            } as any;
          })(),
        },
      };
      await RunLogsApiService.updateNurturingPreferences(preferenceId, body);
      setInitialBatchSizeByRowKey({ ...batchSizeByRowKey });
      setInitialDateFilterType(dateFilterType);
      setInitialStartDate(dateFilterType ? startDate : null);
      setInitialEndDate(dateFilterType === 'DATE_RANGE' ? endDate : null);
      setInitialIsAllChecked(isAllChecked);
      setBatchSizesBeforeAll(null);
    } finally {
      setSaveLoading(false);
    }
  }, [
    preferenceId,
    rows,
    batchSizeByRowKey,
    ttl,
    soldWaitDays,
    unsoldWaitDays,
    dateFilterType,
    startDate,
    endDate,
    isAllChecked,
  ]);

  const hasBatchSizeChanges = React.useMemo(
    () => batchSizesChanged(batchSizeByRowKey, initialBatchSizeByRowKey),
    [batchSizeByRowKey, initialBatchSizeByRowKey],
  );

  const hasDateFilterChanges = React.useMemo(
    () =>
      dateFilterType !== initialDateFilterType ||
      startDate !== initialStartDate ||
      endDate !== initialEndDate,
    [dateFilterType, initialDateFilterType, startDate, initialStartDate, endDate, initialEndDate],
  );
  const hasAllSelectionChange = isAllChecked !== initialIsAllChecked;

  return {
    loading,
    refreshLoading,
    error,
    preferenceId,
    rows,
    headerRow,
    ttl,
    soldWaitDays,
    unsoldWaitDays,
    batchSizeByRowKey,
    updateBatchSize,
    isAllChecked,
    onAllCheckedChange: handleAllCheckedChange,
    saveLoading,
    savePreferences,
    canSavePreferences:
      Boolean(preferenceId) &&
      (hasBatchSizeChanges || hasDateFilterChanges || hasAllSelectionChange),
    refetch: fetchPreferences,
    refreshData,
    rawResponse,

    dateFilterType,
    setDateFilterType: handleDateFilterTypeChange,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
  };
}
